import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Select,
  Upload,
  message,
  Card,
  Tag,
  Space,
  Image,
  Divider,
  Row,
  Col,
  Popconfirm,
  Typography,
  Descriptions,
  Badge,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { getUserData } from "../../../hooks/Auth";

const { Title, Text } = Typography;

const EquipmentManagement = () => {
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [certFile, setCertFile] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [uploading, setUploading] = useState(false);
  const user = getUserData();
  const API_BASE_URL = "https://techcrew.pk/ulc/";
  const UPLOAD_URL = `${API_BASE_URL}upload_api.php`;

  useEffect(() => {
    fetchEquipment();
    fetchDropdowns();
  }, []);

  useEffect(() => {
    filterEquipment();
  }, [equipment, searchText, statusFilter]);

  const fetchEquipment = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Equipment.php`);

      // Check response structure
      if (data.status && data.status.code === 200) {
        // Success case - set equipment data
        setEquipment(data.data);
      } else {
        // Error case from API
        const errorMessage =
          data.status?.message || "Failed to fetch equipment data";
        message.error(errorMessage);
      }
    } catch (error) {
      // Network or server error
      console.error("API Error:", error);

      let errorMessage = "Failed to connect to server";
      if (error.response) {
        // Server responded with error status
        errorMessage =
          error.response.data?.status?.message ||
          error.response.data?.message ||
          "Server returned an error";
      } else if (error.request) {
        // Request was made but no response
        errorMessage = "No response from server";
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [cats, comps] = await Promise.all([
        axios.get(`${API_BASE_URL}Get_Categories.php`),
        axios.get(`${API_BASE_URL}Get_Companies.php`),
      ]);
      setCategories(cats.data.data || []);
      setCompanies(comps.data.data || []);
    } catch (error) {
      message.error("Failed to load dropdowns");
      console.error("Error:", error);
    }
  };

  const filterEquipment = () => {
    let filtered = [...equipment];

    if (statusFilter) {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.equipment_name.toLowerCase().includes(searchLower) ||
          (item.company_name &&
            item.company_name.toLowerCase().includes(searchLower)) ||
          (item.category_name &&
            item.category_name.toLowerCase().includes(searchLower))
      );
    }

    setFilteredEquipment(filtered);
  };

  // Update the handleImageUpload and handleCertUpload functions
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "equipment"); // This tells PHP which folder to use

    try {
      const response = await axios.post(UPLOAD_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        // Return relative path from uploads folder
        return `uploads/${response.data.filePath}`;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      message.error("Image upload failed");
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleCertUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "certifications"); // Different folder for certs

    try {
      const response = await axios.post(UPLOAD_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        // Return relative path from uploads folder
        return `uploads/${response.data.filePath}`;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      message.error("Certification upload failed");
      console.error("Upload error:", error);
      throw error;
    }
  };

  // Update the handleSubmit function to properly handle file paths
  // const handleSubmit = async () => {
  //   try {
  //     const values = await form.validateFields();

  //     // Upload images first
  //     setUploading(true);

  //     // Upload certification if new file added
  //     let certificationPath = certFile?.url;
  //     if (certFile?.originFileObj) {
  //       certificationPath = await handleCertUpload(certFile.originFileObj);
  //     }

  //     // Upload new images
  //     const uploadedPictures = [];
  //     for (const file of fileList) {
  //       if (file.originFileObj) {
  //         const path = await handleImageUpload(file.originFileObj);
  //         uploadedPictures.push(path);
  //       } else if (file.url) {
  //         // Keep existing paths
  //         uploadedPictures.push(file.url);
  //       }
  //     }

  //     // Prepare form data with correct paths
  //     const formData = {
  //       ...values,
  //       price: parseFloat(values.price),
  //       certification_picture: certificationPath || null,
  //       pictures: uploadedPictures,
  //       delete_existing_pictures: !!editingId,
  //     };

  //     if (editingId) {
  //       formData.equipment_id = editingId;
  //     }

  //     const response = await axios.post(
  //       `${API_BASE_URL}AddUpdate_Equipment.php`,
  //       formData,
  //       {
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     if (response.data.status.code === 200) {
  //       message.success(response.data.status.message);
  //       setModalVisible(false);
  //       fetchEquipment();
  //     } else {
  //       throw new Error(response.data.status.message);
  //     }
  //   } catch (error) {
  //     console.error("Submission error:", error);
  //     message.error(
  //       error.response?.data?.status?.message ||
  //         error.message ||
  //         "Error saving equipment"
  //     );
  //   } finally {
  //     setUploading(false);
  //   }
  // };
  // Update the handleSubmit function to properly handle file paths

  const [deletedFiles, setDeletedFiles] = useState([]);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Upload images first
      setUploading(true);

      // Upload certification if new file added
      let certificationPath = selectedEquipment?.certification_picture || null;
      if (certFile?.originFileObj) {
        certificationPath = await handleCertUpload(certFile.originFileObj);
      } else if (!certFile && selectedEquipment?.certification_picture) {
        // If certification was removed
        certificationPath = null;
      }

      // Handle images - separate existing and new files
      const existingPictures = fileList
        .filter((file) => file.uid.startsWith("-")) // Existing files have negative uid
        .map(
          (file) =>
            file.response?.url ||
            file.url.replace("https://www.techcrew.pk/ulc/", "")
        );

      const newPictures = [];
      for (const file of fileList) {
        if (!file.uid.startsWith("-") && file.originFileObj) {
          const path = await handleImageUpload(file.originFileObj);
          newPictures.push(path);
        }
      }

      // Combine existing and new pictures
      const allPictures = [...existingPictures, ...newPictures];

      // Prepare form data with correct paths
      const formData = {
        ...values,
        price: parseFloat(values.price),
        hourprice: parseFloat(values.hourprice),
        certification_picture: certificationPath,
        pictures: newPictures,
        createdby: user?.user?.role_display,
      };

      if (editingId) {
        formData.equipment_id = editingId;
      }

      const response = await axios.post(
        `${API_BASE_URL}AddUpdate_Equipment.php`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status.code === 200) {
        message.success(response.data.status.message);
        setModalVisible(false);
        setDeletedFiles([]);
        fetchEquipment();
      } else {
        throw new Error(response.data.status.message);
      }
    } catch (error) {
      console.error("Submission error:", error);
      message.error(
        error.response?.data?.status?.message ||
          error.message ||
          "Error saving equipment"
      );
    } finally {
      setUploading(false);
    }
  };
  const handleDelete = async (id) => {
      try {
        const { data } = await axios.post(`${API_BASE_URL}Delete_Equipment.php`, {
          equipment_id: id,
        });
  
        if (data.status.code === 200) {
          message.success("Equipment deactivated");
          fetchEquipment();
        } else {
          throw new Error(data.message);
        }
      } catch (error) {
        message.error(error.message || "Deactivation failed");
      }
    };
  
  const columns = [
    {
      title: "Equipment",
      dataIndex: "equipment_name",
      key: "name",
      render: (text, record) => (
        <Space>
          {record.pictures?.[0] && (
            <Image
              src={`https://techcrew.pk/ulc/${record.pictures[0]}`}
              width={50}
              height={50}
              style={{ borderRadius: 4 }}
              preview={false}
            />
          )}
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary">{record.company_name}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category",
      render: (text) => <Tag color="blue">{text || "-"}</Tag>,
    },
    {
      title: "Year",
      dataIndex: "model_year",
      key: "year",
      render: (year) => year || "-",
      align: "center",
    },
    {
      title: "Day Rent (SAR)",
      dataIndex: "price",
      key: "price",
      render: (price) => price?.toLocaleString("en-US"),
      align: "right",
    },
    {
      title: "Hour Rent (SAR)",
      dataIndex: "hourprice",
      key: "hourprice",
      render: (hourprice) => hourprice?.toLocaleString("en-US"),
      align: "right",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Badge
          status={status === "Active" ? "success" : "error"}
          text={status}
        />
      ),
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedEquipment(record);
              setViewModalVisible(true);
            }}
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingId(record.equipment_id);
              form.setFieldsValue({
                ...record,
                price: record.price.toString(),
              });

              // Set existing pictures for editing
              setFileList(
                record.pictures?.map((url, index) => ({
                  uid: `-${index}`, // negative index for existing files
                  name: url.split("/").pop(), // extract filename
                  status: "done", // mark as uploaded
                  url: `https://techcrew.pk/ulc/${url}`,
                  response: { url }, // store the relative path in response
                })) || []
              );

              // Set certification file if exists
              if (record.certification_picture) {
                setCertFile({
                  uid: "-cert",
                  name: record.certification_picture.split("/").pop(),
                  status: "done",
                  url: `https://techcrew.pk/ulc/${record.certification_picture}`,
                  response: { url: record.certification_picture },
                });
              } else {
                setCertFile(null);
              }

              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Deactivate this equipment?"
            onConfirm={() => handleDelete(record.equipment_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
      align: "center",
    },
  ];

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4}>Equipment Management</Title>}
        bordered={false}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setEditingId(null);
              setFileList([]);
              setCertFile(null);
              setModalVisible(true);
            }}
          >
            Add Equipment
          </Button>
        }
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Input
              placeholder="Search equipment..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={12}>
            <Select
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
                { value: "", label: "All Statuses" },
              ]}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredEquipment}
          rowKey="equipment_id"
          loading={loading}
          scroll={{ x: true }}
          bordered
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingId ? "Edit Equipment" : "Add New Equipment"}
        visible={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
        destroyOnClose
        okText={editingId ? "Update" : "Add"}
        confirmLoading={uploading}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="equipment_name"
                label="Equipment Name"
                rules={[{ required: true, message: "Please enter name" }]}
              >
                <Input placeholder="Enter equipment name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="company_id"
                label="Company"
                rules={[{ required: true, message: "Please select company" }]}
              >
                <Select
                  placeholder="Select company"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {companies.map((c) => (
                    <Select.Option key={c.company_id} value={c.company_id}>
                      {c.company_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category_id"
                label="Category"
                rules={[{ required: true, message: "Please select category" }]}
              >
                <Select
                  placeholder="Select category"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categories.map((c) => (
                    <Select.Option key={c.category_id} value={c.category_id}>
                      {c.category_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="model_year" label="Model Year">
                <Input placeholder="YYYY" maxLength={4} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="price"
                label="Day Rent (SAR)"
                rules={[{ required: true, message: "Please enter day rent" }]}
              >
                <Input type="number" min="0" step="0.01" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="hourprice"
                label="Hour Rent"
                rules={[{ required: true, message: "Please enter hour rent" }]}
              >
                <Input type="number" min="0" step="0.01" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} maxLength={250} />
          </Form.Item>

          <Form.Item name="status" label="Status" initialValue="Active">
            <Select>
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>

          <Divider orientation="left">Media</Divider>

          <Form.Item label="Certification Document">
            <Upload
              fileList={certFile ? [certFile] : []}
              beforeUpload={() => false}
              onChange={({ file }) => setCertFile(file)}
              accept=".jpg,.jpeg,.png,.pdf"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload Certification</Button>
            </Upload>
          </Form.Item>

          <Form.Item label="Equipment Images">
            <Upload
              listType="picture-card"
              fileList={fileList}
              beforeUpload={(file) => {
                // Prevent automatic upload
                return false;
              }}
              onChange={({ fileList: newFileList }) => {
                // Combine existing and new files (max 8)
                const existingFiles = fileList.filter((file) =>
                  file.uid.startsWith("-")
                );
                const newFiles = newFileList
                  .filter((file) => !file.uid.startsWith("-"))
                  .slice(0, 8 - existingFiles.length);

                setFileList([...existingFiles, ...newFiles]);
              }}
              onRemove={async (file) => {
                try {
                  // If it's an existing file (has negative uid), delete it from server
                  if (file.uid.startsWith("-")) {
                    setUploading(true);
                    await axios.post(
                      `${API_BASE_URL}Delete_EquipmentPicture.php`,
                      {
                        picture_url: file.response.url,
                        equipment_id: editingId,
                      }
                    );

                    // Remove from fileList
                    setFileList((prev) =>
                      prev.filter((f) => f.uid !== file.uid)
                    );
                    message.success("Picture deleted successfully");
                  } else {
                    // For new files that weren't uploaded yet, just remove from fileList
                    setFileList((prev) =>
                      prev.filter((f) => f.uid !== file.uid)
                    );
                  }
                  return true; // allow removal
                } catch (error) {
                  message.error("Failed to delete picture");
                  console.error("Delete error:", error);
                  return false; // prevent removal
                } finally {
                  setUploading(false);
                }
              }}
              accept="image/*"
              multiple
            >
              {fileList.length < 8 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal */}
      {/* View Modal */}
      <Modal
        title={<Title level={4}>Equipment Details</Title>}
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedEquipment && (
          <>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Name" span={2}>
                <Text strong>{selectedEquipment.equipment_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                {selectedEquipment.company_name}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                <Tag color="blue">{selectedEquipment.category_name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Model Year">
                {selectedEquipment.model_year || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Day Rent">
                SAR {selectedEquipment.price?.toLocaleString("en-US")}
              </Descriptions.Item>
              <Descriptions.Item label="Hour Rent">
                SAR {selectedEquipment.hourprice?.toLocaleString("en-US")}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={
                    selectedEquipment.status === "Active" ? "success" : "error"
                  }
                  text={selectedEquipment.status}
                />
              </Descriptions.Item>
              {selectedEquipment.remarks && (
                <Descriptions.Item label="Remarks" span={2}>
                  {selectedEquipment.remarks}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">
              Images ({selectedEquipment.pictures?.length || 0})
            </Divider>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
              {selectedEquipment.pictures?.map((img, index) => (
                <div key={index} style={{ width: "calc(33.33% - 16px)" }}>
                  <Image
                    src={`https://unitedlimitedcompany.com/ulc/${img}`}
                    style={{
                      width: "100%",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "1px solid #f0f0f0",
                    }}
                    preview={{
                      src: `https://techcrew.pk/ulc/${img}`,
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Certification Image */}
            {selectedEquipment.certification_picture && (
              <>
                <Divider orientation="left">Certification Document</Divider>
                <div style={{ marginTop: "16px" }}>
                  <Image
                    src={`https://techcrew.pk/ulc/${selectedEquipment.certification_picture}`}
                    style={{
                      width: "100%",
                      maxHeight: "300px",
                      objectFit: "contain",
                    }}
                    preview={{
                      src: `https://techcrew.pk/ulc/${selectedEquipment.certification_picture}`,
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default EquipmentManagement;
